import { useCallback, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import { Hinweis } from "@kundeninfo/ui/me-design";
import type { TicketDetail } from "@kundeninfo/api-client";

/**
 * DER SOFORT-SCHREIBWEG DER TICKET-UNTEROBJEKTE (Ruling B7): Kommentare, Teilaufgaben, Kunden,
 * Ansprechpartner, Bezüge und Anhänge sind ohne „Speichern“ im Fuß sofort gespeichert. Jede
 * Schreibmethode antwortet mit dem vollen Detail (Aufgabe 10); der Weg reicht es über `onNeu`
 * an den Dialog. Aufgabe 23 (Reiter „Verknüpfungen“) nimmt denselben Haken.
 *
 *  - **409** (`VERSIONSKONFLIKT`, `current` ist hier das Unterobjekt, nicht das Ticket): Das
 *    Detail wird über `nachladen` neu geholt und gezeigt, dazu die Meldung „Das Ticket wurde
 *    inzwischen geändert“ — nie ein stiller Überschreiber (Review-Fokus 2).
 *  - **422** mit `fields`: Befunde je Feldname in `feldFehler`; Befunde zu Feldern, die der
 *    Aufrufer nicht unter `felder` zeigt, stehen in der Meldung — keiner verschwindet still.
 *  - **403**, **413** und alles andere: die Meldung des Servers bzw. ein allgemeiner Text als
 *    Hinweis. `versuchen` liefert den Ausgang ohne Anzeige (Sammelwege, Mehrfach-Upload).
 */

export type SchreibMeldung = { art: "fehler" | "warnung"; text: string };

/** Ausgang eines Schreibwegs, ohne ihn anzuzeigen (für Sammelwege wie den Mehrfach-Upload). */
export type SchreibErgebnis = { ok: boolean; meldung: SchreibMeldung | null; feldFehler: Record<string, string> };

export type Schreibweg<T = TicketDetail> = {
  /** Führt `arbeit` aus und zeigt den Ausgang (Meldung, Befunde); `true`, wenn sie gelang. */
  schreiben(arbeit: () => Promise<T>, sonst?: string): Promise<boolean>;
  /** Wie `schreiben`, zeigt aber nichts an — der Aufrufer sammelt (`melden`). */
  versuchen(arbeit: () => Promise<T>, sonst?: string): Promise<SchreibErgebnis>;
  /** Mindestens ein Schreibweg läuft (für Sperren und „lädt hoch …“). */
  laeuft: boolean;
  meldung: SchreibMeldung | null;
  feldFehler: Record<string, string>;
  /** Eine eigene Meldung setzen oder räumen (Vorschau gescheitert, gesammelte Fehler). */
  melden(meldung: SchreibMeldung | null): void;
  /** Den Befund EINES Feldes räumen — beim Tippen in dieses Feld. */
  feldRaeumen(name: string): void;
};

export const GEAENDERT = "Das Ticket wurde inzwischen geändert — der aktuelle Stand ist geladen.";

const SONST = "Das hat nicht geklappt. Bitte noch einmal versuchen.";
const OHNE_BEFUND: Record<string, string> = {};

export function useSchreibweg<T = TicketDetail>(
  onNeu: (d: T) => void,
  optionen: { nachladen?: () => Promise<T>; felder?: readonly string[] } = {},
): Schreibweg<T> {
  const [laufend, setLaufend] = useState(0);
  const [meldung, setMeldung] = useState<SchreibMeldung | null>(null);
  const [feldFehler, setFeldFehler] = useState<Record<string, string>>(OHNE_BEFUND);
  const neuester = useRef({ onNeu, optionen });
  neuester.current = { onNeu, optionen };

  const versuchen = useCallback(async (arbeit: () => Promise<T>, sonst = SONST): Promise<SchreibErgebnis> => {
    const fehler = (text: string): SchreibErgebnis => ({ ok: false, meldung: { art: "fehler", text }, feldFehler: OHNE_BEFUND });
    setLaufend((n) => n + 1);
    try {
      neuester.current.onNeu(await arbeit());
      return { ok: true, meldung: null, feldFehler: OHNE_BEFUND };
    } catch (e) {
      if (!(e instanceof ApiError)) return fehler(sonst);
      if (e.status === 409) {
        const { nachladen } = neuester.current.optionen;
        try {
          if (nachladen !== undefined) neuester.current.onNeu(await nachladen());
          return { ok: false, meldung: { art: "warnung", text: GEAENDERT }, feldFehler: OHNE_BEFUND };
        } catch {
          return fehler("Das Ticket wurde inzwischen geändert und ließ sich nicht neu laden.");
        }
      }
      if (e.status === 422 && e.fields !== undefined && e.fields.length > 0) {
        const gezeigt = neuester.current.optionen.felder ?? [];
        const map: Record<string, string> = {};
        const rest: string[] = [];
        for (const f of e.fields) {
          if (gezeigt.includes(f.field)) map[f.field] ??= f.message;
          else rest.push(f.message);
        }
        return { ok: false, feldFehler: map,
                 meldung: rest.length === 0 ? null : { art: "fehler", text: [...new Set(rest)].join("\n") } };
      }
      if (e.status === 403) return fehler(e.message !== "" ? e.message : "Dafür fehlt die Berechtigung.");
      if (e.status === 413) return fehler(e.message !== "" ? e.message : "Die Datei ist zu groß.");
      return fehler(e.message !== "" ? e.message : sonst);
    } finally {
      setLaufend((n) => n - 1);
    }
  }, []);

  const schreiben = useCallback(async (arbeit: () => Promise<T>, sonst?: string): Promise<boolean> => {
    const ergebnis = await versuchen(arbeit, sonst);
    setMeldung(ergebnis.meldung);
    setFeldFehler(ergebnis.feldFehler);
    return ergebnis.ok;
  }, [versuchen]);

  const feldRaeumen = useCallback((name: string) => {
    setFeldFehler((alt) => {
      if (alt[name] === undefined) return alt;
      const neu = { ...alt };
      delete neu[name];
      return neu;
    });
  }, []);

  return { schreiben, versuchen, laeuft: laufend > 0, meldung, feldFehler, melden: setMeldung, feldRaeumen };
}

/** Die Meldung eines Schreibwegs als ME-Hinweis (dort, wo gerade gearbeitet wird). */
export function SchreibHinweis(p: { meldung: SchreibMeldung | null }): ReactElement | null {
  return p.meldung === null ? null
    : <Hinweis art={p.meldung.art}><span className="td__meldung">{p.meldung.text}</span></Hinweis>;
}
